import {
  Component,
  ElementRef,
  Injector,
  afterNextRender,
  computed,
  inject,
  input,
  signal,
} from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';

import { environment } from '../../../environments/environment';
import { LinksEnvironment } from '../../../environments/environment.model';
import { AuthService } from '../../services/auth.service';

/** One destination of the account menu — always another site, opened aside. */
export interface AccountLink {
  readonly id: 'umc' | 'profile' | 'password' | 'help';
  readonly icon: string;
  readonly labelKey: string;
  /** A second, quieter line under the label. */
  readonly hintKey?: string;
  readonly href: string;
  /** Drawn in the brand colour, with an "opens elsewhere" mark. */
  readonly accent?: boolean;
}

/**
 * The signed-in person at the foot of the sidebar, and the menu behind them:
 * the other Saphir surfaces they may need (UMC, their profile there, the
 * password change at Identity, support) and the way out.
 *
 * A popup over the trigger rather than a page: every entry leaves the shell,
 * so a screen of its own would be a screen with nothing on it. It behaves as
 * a menu button — `aria-haspopup`, arrow keys between entries, Escape and a
 * click elsewhere close it, focus returns to the trigger.
 *
 * On the rail only the avatar remains; the popup keeps its full width and
 * overhangs the content, which is what a rail flyout does everywhere else.
 */
@Component({
  selector: 'app-user-menu',
  imports: [TranslatePipe],
  templateUrl: './user-menu.html',
  styleUrl: './user-menu.scss',
  host: {
    class: 'user-menu',
    '[class.user-menu--rail]': 'rail()',
    '(document:pointerdown)': 'onDocumentPointerDown($event)',
    '(keydown.escape)': 'close(true)',
    '(focusout)': 'onFocusOut($event)',
  },
})
export class UserMenu {
  /** Icon rail instead of the full column — set by the sidebar. */
  readonly rail = input(false);

  protected readonly auth = inject(AuthService);
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly injector = inject(Injector);

  protected readonly open = signal(false);

  protected readonly popupId = 'user-menu-popup';

  /** Addresses are build-time; what is absent is simply not offered. */
  protected readonly links = accountLinks(environment.links);

  /** "SW"-style monogram for the avatar — the first letters of the name. */
  protected readonly initials = computed(() => {
    const name = this.auth.user()?.displayName ?? '';

    return name
      .split(/[\s._@-]+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]!.toUpperCase())
      .join('');
  });

  protected toggle(): void {
    if (this.open()) {
      this.close(true);

      return;
    }

    this.open.set(true);

    // The popup enters the DOM with the next render; focus goes to its first
    // entry, as a menu button does when opened.
    afterNextRender(() => this.menuItems()[0]?.focus(), { injector: this.injector });
  }

  /** `returnFocus` when the close is a keyboard act — the trigger takes it back. */
  protected close(returnFocus: boolean): void {
    if (!this.open()) {
      return;
    }

    this.open.set(false);

    if (returnFocus) {
      this.host.nativeElement.querySelector<HTMLElement>('.user-menu__trigger')?.focus();
    }
  }

  protected signOut(): void {
    this.close(false);
    this.auth.logout();
  }

  protected onDocumentPointerDown(event: PointerEvent): void {
    if (!(event.target instanceof Node && this.host.nativeElement.contains(event.target))) {
      this.close(false);
    }
  }

  /** Focus moving anywhere outside the component means the menu is done. */
  protected onFocusOut(event: FocusEvent): void {
    const next = event.relatedTarget;

    if (!(next instanceof Node && this.host.nativeElement.contains(next))) {
      this.close(false);
    }
  }

  /** Arrow keys walk the entries, wrapping; Home and End jump. */
  protected onMenuKeydown(event: KeyboardEvent): void {
    const items = this.menuItems();

    if (items.length === 0) {
      return;
    }

    const current = items.findIndex((item) => item === event.target);
    let next: number | undefined;

    switch (event.key) {
      case 'ArrowDown':
        next = (current + 1) % items.length;
        break;
      case 'ArrowUp':
        next = (current - 1 + items.length) % items.length;
        break;
      case 'Home':
        next = 0;
        break;
      case 'End':
        next = items.length - 1;
        break;
    }

    if (next !== undefined) {
      event.preventDefault();
      items[next]!.focus();
    }
  }

  private menuItems(): HTMLElement[] {
    return Array.from(this.host.nativeElement.querySelectorAll<HTMLElement>('[role="menuitem"]'));
  }
}

/**
 * The menu entries an environment's addresses allow, in the order the design
 * shows them. Pure, so the mapping can be checked without a component.
 */
export function accountLinks(links: LinksEnvironment | undefined): AccountLink[] {
  const umc = trimSlash(links?.umcConsoleUrl);
  const identity = trimSlash(links?.identityWebUrl);
  const help = links?.helpUrl;

  // Built by pushing rather than as an array of `cond && {...}` entries. With `string | undefined`
  // on the left, `&&` yields the EMPTY STRING when the address is configured as "", so the array
  // type became `AccountLink | "" | undefined` — which no `link is AccountLink` predicate can
  // narrow, because "" is not assignable to it. Pushing keeps the array `AccountLink[]` throughout
  // and needs no filter at all.
  const items: AccountLink[] = [];

  if (umc) {
    items.push(
      {
        id: 'umc',
        icon: 'pi pi-external-link',
        labelKey: 'user.openUmc',
        hintKey: 'user.openUmcHint',
        href: umc,
        accent: true,
      },
      {
        id: 'profile',
        icon: 'pi pi-user',
        labelKey: 'user.profile',
        href: `${umc}/settings`,
      },
    );
  }

  if (identity) {
    items.push({
      id: 'password',
      icon: 'pi pi-key',
      labelKey: 'user.changePassword',
      href: `${identity}/reset-password`,
    });
  }

  if (help) {
    items.push({
      id: 'help',
      icon: 'pi pi-question-circle',
      labelKey: 'user.help',
      href: help,
    });
  }

  return items;
}

function trimSlash(url: string | undefined): string | undefined {
  return url?.replace(/\/+$/, '');
}
